import React from 'react';
import { motion } from 'framer-motion';
import ScrollReveal from './ScrollReveal';
import { JALSA_LEGACY } from '../utils/cinematicData';
import { Award, Heart, Users, TrendingUp, Star } from 'lucide-react';

const JalsaLegacy = () => {
  const valueIcons = {
    "Authentic Indian Hospitality": Heart,
    "Uncompromising Quality": Star,
    "Sustainable Practices": TrendingUp,
    "Community Engagement": Users,
    "Innovation in Service": Award
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231B3A6B' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal direction="up" className="text-center mb-20">
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
            <Award className="mx-4 w-6 h-6 text-gold-400" />
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-serif text-navy-900 mb-6">
            The Jalsa Legacy
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto font-light leading-relaxed">
            {JALSA_LEGACY.story}
          </p>

          <div className="mt-8 flex items-center justify-center space-x-12">
            <div className="text-center">
              <div className="text-5xl font-serif text-gold-400 mb-2">{JALSA_LEGACY.founded}</div>
              <div className="text-gray-600 font-light">Founded</div>
            </div>
            <div className="h-16 w-px bg-gold-400" />
            <div className="text-center">
              <div className="text-5xl font-serif text-gold-400 mb-2">{JALSA_LEGACY.yearsOfExcellence}</div>
              <div className="text-gray-600 font-light">Years of Excellence</div>
            </div>
          </div>
        </ScrollReveal>

        {/* Timeline */}
        <div className="max-w-6xl mx-auto mt-20">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-gold-400 via-navy-600 to-gold-400" />

            {JALSA_LEGACY.milestones.map((milestone, index) => (
              <ScrollReveal 
                key={index}
                direction={index % 2 === 0 ? 'right' : 'left'}
                delay={index * 0.2}
                className="mb-16"
              >
                <div className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  {/* Content */}
                  <div className="w-5/12">
                    <motion.div 
                      whileHover={{ scale: 1.05 }}
                      className="bg-white p-8 rounded-lg shadow-xl border border-gold-400/20 hover:border-gold-400 transition-all duration-300"
                    >
                      <div className="text-4xl font-serif text-gold-400 mb-3">{milestone.year}</div>
                      <h3 className="text-2xl font-serif text-navy-900 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                    </motion.div>
                  </div>

                  {/* Center Dot */}
                  <div className="w-2/12 flex justify-center">
                    <motion.div 
                      whileHover={{ scale: 1.5 }}
                      className="w-6 h-6 bg-gold-400 rounded-full border-4 border-white shadow-lg z-10"
                    />
                  </div>

                  {/* Empty Space */}
                  <div className="w-5/12" />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>

        {/* Vision & Values */}
        <ScrollReveal direction="up" delay={0.3} className="mt-24">
          <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl p-12 text-white">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Vision */}
              <div>
                <h3 className="text-3xl font-serif mb-6 text-gold-400">Our Vision</h3>
                <p className="text-lg leading-relaxed text-gray-200">
                  {JALSA_LEGACY.vision}
                </p>
              </div>

              {/* Values */}
              <div>
                <h3 className="text-3xl font-serif mb-6 text-gold-400">Our Values</h3>
                <div className="space-y-4">
                  {JALSA_LEGACY.values.map((value, index) => {
                    const Icon = valueIcons[value] || Star;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center space-x-3 group hover:translate-x-2 transition-transform duration-300"
                      >
                        <Icon className="w-5 h-5 text-gold-400 flex-shrink-0" />
                        <span className="text-gray-200 group-hover:text-gold-400 transition-colors duration-300">
                          {value}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default JalsaLegacy;
